<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import type { TableExtParagraph } from '../../utils/paragraph-schema/paragraphs'

type TableExtCell = NonNullable<NonNullable<TableExtParagraph['tableExt']>['rows']>[number][number]

const props = defineProps<{
  tableExt: TableExtParagraph['tableExt']
}>()

const rows = computed(() => props.tableExt?.rows ?? [])
const alternatingRowColor = computed(() => props.tableExt?.alternatingRowColor ?? false)
const title = computed(() => props.tableExt?.title)

const tableTag = ref<HTMLTableElement | null>(null)
const enableScrollManagement = ref(true)
const scrollDirection = ref<'up' | 'down'>()
const lastStickyScrollDirection = ref<'up' | 'down'>()
const allowBlocking = ref(true)
const touchStartY = ref(0)
const touchStartX = ref(0)
const blockingTimeout = ref<ReturnType<typeof setTimeout>>()
const headerOffset = 80

const colWidths = computed(() => rows.value[0]?.map((cell) => cell.attrs?.colwidth?.[0]) ?? [])

onMounted(() => {
  const tableHeight = tableTag.value?.getBoundingClientRect().height
  enableScrollManagement.value = (tableHeight ?? 0) > (window.innerHeight - headerOffset)
})

function getCellStyles (rowIndex: number, colIndex: number, colorSchema: number | null | undefined, isHeadCell: boolean, textAlign: string) {
  return {
    'bx-typo--content-continuous-text': true,
    'bx-table-cell-sticky': colIndex === 0 || rowIndex === 0,
    'bx-table-cell-sticky--row': isHeadCell && (rowIndex === 0 && colIndex !== 0),
    'bx-table-cell-sticky--col': colIndex === 0,
    'bx-table-cell-sticky--corner': isHeadCell && (rowIndex === 0 && colIndex === 0),
    'bx-table-cell-color-schema': !!colorSchema,
    [`bx-table-cell-color-schema--${colorSchema}`]: !!colorSchema,
    [`bx-table-cell--${textAlign}`]: textAlign !== 'left'
  }
}

function getTableCellBinding (rowIndex: number, colIndex: number, cell: TableExtCell) {
  return {
    class: getCellStyles(rowIndex, colIndex, cell?.attrs?.colorSchema, cell.type === 'tableHeader', cell?.attrs?.textAlign ?? 'left'),
    colspan: (cell?.attrs?.colspan ?? 0) > 1 ? cell.attrs?.colspan : undefined,
    rowspan: (cell?.attrs?.rowspan ?? 0) > 1 ? cell.attrs?.rowspan : undefined
  }
}

function onTouchStart (e: TouchEvent) {
  touchStartY.value = e.touches[0].clientY
  touchStartX.value = e.touches[0].clientX
}

function manageScrollBehavior (event: WheelEvent | TouchEvent) {
  const wrapper = tableTag.value
  if (!wrapper || !enableScrollManagement.value) return

  // Determine if this is a touch event
  const isTouchEvent = !!(event as TouchEvent).touches?.length

  // Get current touch/mouse positions
  const touchY = isTouchEvent ? (event as TouchEvent).touches[0].clientY : null
  const touchX = isTouchEvent ? (event as TouchEvent).touches[0].clientX : null

  // Calculate scroll delta (vertical and horizontal)
  const deltaY = isTouchEvent
    ? touchStartY.value - touchY!
    : (event as WheelEvent).deltaY
  const deltaX = isTouchEvent
    ? touchStartX.value - touchX!
    : (event as WheelEvent).deltaX

  // Determine scroll direction
  scrollDirection.value = deltaY < 0 ? 'up' : 'down'

  // Ignore horizontal scrolls or zero vertical movement
  if (Math.abs(deltaY) === 0 || Math.abs(deltaX) > 10) return

  // Cache wrapper scroll measurements
  const scrollTop = wrapper.scrollTop
  const clientHeight = wrapper.clientHeight
  const atTop = scrollTop === 0
  const atBottom = scrollTop + clientHeight >= wrapper.scrollHeight - 1

  const wrapperTop = wrapper.getBoundingClientRect().top
  const scrollAmount = wrapperTop - headerOffset

  /* === Snap scrolling: adjust page scroll once per scroll direction === */
  if (lastStickyScrollDirection.value !== scrollDirection.value) {
    window.scrollTo({ top: window.scrollY + scrollAmount, behavior: 'smooth' })
    event.preventDefault()

    // Enable temporary scroll blocking
    allowBlocking.value = true
    document.body.classList.add('bx-js-scroll-lock')
    lastStickyScrollDirection.value = scrollDirection.value
  } else {
    /* === Table scroll: allow scrolling inside table if not stuck at edges === */
    if (!atTop && !atBottom) {
      wrapper.scrollTop += deltaY
      event.preventDefault()
    } else {
      /* === Prevent page scroll when table is at top/bottom === */
      if (allowBlocking.value && (
        (atTop && scrollDirection.value === 'up') ||
        (atBottom && scrollDirection.value === 'down')
      )) {
        if (!blockingTimeout.value) {
          // Set a timeout to release scroll lock after short delay
          blockingTimeout.value = setTimeout(() => {
            document.body.classList.remove('bx-js-scroll-lock')
            allowBlocking.value = false
            blockingTimeout.value = undefined
          }, 1000)
        }
        event.preventDefault()
      }
    }
  }

  // Store last touch position for next delta calculation
  touchStartY.value = touchY ?? touchStartY.value
  touchStartX.value = touchX ?? touchStartX.value
}
</script>

<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <section class="bx-content bx-table bx-typo--table"
           :class="{ 'bx-table--alternate-row-colors': alternatingRowColor }">
    <span v-if="title"
          class="bx-table__headline">
      {{ title }}
    </span>

    <table v-if="rows.length"
           ref="tableTag"
           :aria-label="title"
           @wheel="manageScrollBehavior"
           @touchstart="onTouchStart"
           @touchmove="(e) => manageScrollBehavior(e as unknown as TouchEvent)">
      <colgroup>
        <col v-for="(col, colIndex) in colWidths"
             :key="`col-${colIndex}`"
             :style="col ? 'width: ' + col + 'px;' : 'min-width: 25px;'">
      </colgroup>
      <tbody>
        <tr v-for="(row, rowIndex) in rows"
            :key="`row-${rowIndex}`">
          <template v-for="(cell, colIndex) in row"
                    :key="`row-${rowIndex}-col-${colIndex}`">
            <th v-if="cell.type === 'tableHeader'"
                v-bind="getTableCellBinding(rowIndex, colIndex, cell)"
                v-html="cell.content" />
            <td v-else
                v-bind="getTableCellBinding(rowIndex, colIndex, cell)"
                v-html="cell.content" />
          </template>
        </tr>
      </tbody>
    </table>
  </section>
</template>
